// EnrollFacePage — public /enroll-face?token=… landing.
//
// Spec: docs/specs/location-tasks.md §7.1 (FaceID-style 5-pose enroll).
//
// Flow:
//   1. Validate token → POST /v1/face/enroll/start
//   2. Lazy-load face-api.js from jsDelivr CDN (≈ 700KB lib + ≈ 6MB models
//      hosted at justadudewhohacks.github.io/face-api.js/models/).
//   3. Open camera (front/user-facing) via getUserMedia().
//   4. Five-pose ring guide:
//      pose 0 — 正視 (yaw≈0, pitch≈0)
//      pose 1 — 右轉 (yaw > 25°)
//      pose 2 — 左轉 (yaw < -25°)
//      pose 3 — 抬頭 (pitch < -15°)
//      pose 4 — 低頭 (pitch > 15°)
//      Pose detection uses face-api.js's faceLandmark68Net + custom yaw/pitch
//      estimation from landmark positions (no MediaPipe dependency required;
//      keeps bundle smaller).
//   5. For each pose: detect face + descriptor + thumbnail (160×160 jpeg).
//   6. Submit POST /v1/face/enroll/finish with 5 descriptors + thumbnails.
//
// Privacy: descriptors are 128-dim Float32 vectors (≈ 0.5KB each), the
// only persistent server-side storage. Thumbnails are 160×160 jpegs for
// admin visual review only. Original camera frames never leave the device.
//
// Bundle: face-api.js + tfjs lazy loaded from jsDelivr → 0 added to main
// bundle. Models loaded from face-api.js GitHub Pages on first visit.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Smile, Camera, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { resolveApiBase } from "@nexus/frontend-shared/api-client";

// Host-aware API base (NEXUS group 8): nexus production default, host-matched
// in the browser, VITE_API_BASE dev override.
const API_BASE = resolveApiBase();

// face-api.js + models served from jsDelivr / project's GH Pages
const FACEAPI_LIB_URL = "https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js";
const FACEAPI_MODELS_URL = "https://justadudewhohacks.github.io/face-api.js/models";

interface FaceApi {
  nets: {
    tinyFaceDetector: { loadFromUri(u: string): Promise<void> };
    faceLandmark68Net: { loadFromUri(u: string): Promise<void> };
    faceRecognitionNet: { loadFromUri(u: string): Promise<void> };
  };
  TinyFaceDetectorOptions: new (opts?: { inputSize?: number; scoreThreshold?: number }) => unknown;
  detectSingleFace(input: HTMLVideoElement | HTMLCanvasElement, opts?: unknown): {
    withFaceLandmarks(): { withFaceDescriptor(): Promise<{
      detection: { box: { x: number; y: number; width: number; height: number } };
      landmarks: { positions: { x: number; y: number }[] };
      descriptor: Float32Array;
    } | undefined> };
  };
}

declare global {
  interface Window { faceapi?: FaceApi }
}

type Phase = "init" | "validating" | "loading_ml" | "camera_starting" | "ready" | "capturing" | "submitting" | "done" | "error";

/** Stage flags for the per-model progress UI shown during loading_ml. */
type MlStage = "lib" | "detector" | "landmark" | "recognition";
type StageState = "pending" | "loading" | "done" | "failed";

type StartResp = {
  ok?: true;
  officer_id?: string;
  display_name?: string;
  already_enrolled?: boolean;
  error?: string;
};

/** 8 poses arranged as a ring around the face (FaceID-style).
 *  `angleDeg` follows the SVG convention — 0° = right (3 o'clock),
 *  going clockwise. After estimatePose flips the yaw sign, the
 *  yaw/pitch space matches: yaw > 0 = subject's right (image-right
 *  in the mirrored video), pitch > 0 = down. So the pose's angleDeg
 *  is also the atan2(pitch, yaw) direction the user has to face for
 *  the head-direction pointer to land on this segment.
 *
 *  Capture is now driven by the pointer (computed from current
 *  yaw/pitch) overlapping a segment — no per-pose check function. */
interface PoseDef {
  key: string;
  angleDeg: number;
  label: string;
  hint: string;
}
const POSES: PoseDef[] = [
  { key: "right",      angleDeg: 0,    label: "右",   hint: "請慢慢將頭轉向右側" },
  { key: "down_right", angleDeg: 45,   label: "右下", hint: "請慢慢將頭轉向右下方" },
  { key: "down",       angleDeg: 90,   label: "下",   hint: "請慢慢將頭低下" },
  { key: "down_left",  angleDeg: 135,  label: "左下", hint: "請慢慢將頭轉向左下方" },
  { key: "left",       angleDeg: 180,  label: "左",   hint: "請慢慢將頭轉向左側" },
  { key: "up_left",    angleDeg: 225,  label: "左上", hint: "請慢慢將頭轉向左上方" },
  { key: "up",         angleDeg: 270,  label: "上",   hint: "請慢慢將頭抬起" },
  { key: "up_right",   angleDeg: 315,  label: "右上", hint: "請慢慢將頭轉向右上方" },
];

/** Pointer must move at least this far from center before it registers
 *  as "aimed at a direction" (so small twitches don't drift it around).
 *  Magnitude = hypot(yaw, pitch) in degree units. */
const POINTER_MIN_MAG = 11;
/** Half the angular tolerance for capturing a segment. Matches the
 *  half-angular-width of each arc so the pointer triggers exactly when
 *  it visually overlaps the segment. */
const CAPTURE_HALF = 18;

/** Minimum angular distance |a - b| considering 360° wrap. */
function angularDist(a: number, b: number): number {
  const d = Math.abs(((a - b) % 360 + 360) % 360);
  return Math.min(d, 360 - d);
}

export default function EnrollFacePage(): React.ReactElement {
  const [params] = useSearchParams();
  const token = (params.get("token") ?? "").trim();

  const [phase, setPhase] = useState<Phase>("init");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [officerName, setOfficerName] = useState<string | null>(null);
  const [alreadyEnrolled, setAlreadyEnrolled] = useState(false);
  /** Bitset of captured pose indices. Used directly by ring SVG so
   *  React re-renders when a new pose is captured. */
  const [captured, setCaptured] = useState<number[]>([]);
  /** Index of the pose suggested next (closest-uncaptured) — drives the
   *  prompt text. -1 means done. */
  const [nextIdx, setNextIdx] = useState<number>(0);
  /** Index of the pose JUST captured — drives the brief capture-flash
   *  animation on that segment. Reset to null after animation ends. */
  const [flashIdx, setFlashIdx] = useState<number | null>(null);
  /** Where the head is currently aiming, in SVG-ring degrees. null
   *  when head is too close to centre to register a direction. */
  const [pointerAngle, setPointerAngle] = useState<number | null>(null);
  /** Flips true on <video> `onloadedmetadata` (camera stream's first
   *  stable frame). Used to gate the video opacity transition — keeps
   *  the operator from seeing the initial getUserMedia "zoom-in" jitter
   *  while the browser negotiates the final stream resolution. */
  const [videoReady, setVideoReady] = useState(false);
  const [mlStages, setMlStages] = useState<Record<MlStage, StageState>>({
    lib: "pending", detector: "pending", landmark: "pending", recognition: "pending",
  });

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // descriptors[i] / thumbnails[i] sit at the pose index inside POSES[].
  const descriptorsRef = useRef<(Float32Array | null)[]>(Array(POSES.length).fill(null));
  const thumbnailsRef = useRef<(string | null)[]>(Array(POSES.length).fill(null));

  // ── Step 1: validate token ───────────────────────────────────────────
  useEffect(() => {
    if (!token) {
      setErrMsg("缺少註冊連結 token。請聯絡管理員重新發送連結。");
      setPhase("error");
      return;
    }
    if (!/^[0-9a-f]{64}$/i.test(token)) {
      setErrMsg("註冊連結格式錯誤。");
      setPhase("error");
      return;
    }
    setPhase("validating");
    fetch(`${API_BASE}/v1/face/enroll/start`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ token }),
    })
      .then(async (r) => {
        const j = await r.json().catch(() => ({})) as StartResp;
        if (!r.ok) {
          const msg = j.error === "invalid_or_expired_token"
            ? "註冊連結已失效或過期。請聯絡管理員重新發送。"
            : `驗證失敗（${j.error ?? r.status}）`;
          setErrMsg(msg);
          setPhase("error");
          return;
        }
        setOfficerName(j.display_name ?? null);
        setAlreadyEnrolled(!!j.already_enrolled);
        setPhase("loading_ml");
      })
      .catch(() => {
        setErrMsg("無法連線到伺服器，請檢查網路後再試。");
        setPhase("error");
      });
  }, [token]);

  // ── Step 2: lazy-load face-api.js ────────────────────────────────────
  // Loaded sequentially (lib → 3 models in parallel) with per-stage flags
  // so the user sees actual progress instead of an indeterminate spinner.
  useEffect(() => {
    if (phase !== "loading_ml") return;
    let cancelled = false;
    const set = (s: MlStage, v: StageState) => {
      if (cancelled) return;
      setMlStages((prev) => ({ ...prev, [s]: v }));
    };
    void (async () => {
      try {
        if (!window.faceapi) {
          set("lib", "loading");
          await loadScript(FACEAPI_LIB_URL);
        }
        if (!window.faceapi) throw new Error("face-api.js failed to expose globals");
        set("lib", "done");

        set("detector", "loading");
        set("landmark", "loading");
        set("recognition", "loading");
        await Promise.all([
          window.faceapi.nets.tinyFaceDetector.loadFromUri(FACEAPI_MODELS_URL)
            .then(() => set("detector", "done"))
            .catch((e) => { set("detector", "failed"); throw e; }),
          window.faceapi.nets.faceLandmark68Net.loadFromUri(FACEAPI_MODELS_URL)
            .then(() => set("landmark", "done"))
            .catch((e) => { set("landmark", "failed"); throw e; }),
          window.faceapi.nets.faceRecognitionNet.loadFromUri(FACEAPI_MODELS_URL)
            .then(() => set("recognition", "done"))
            .catch((e) => { set("recognition", "failed"); throw e; }),
        ]);
        if (cancelled) return;
        setPhase("camera_starting");
      } catch (e) {
        if (cancelled) return;
        setErrMsg("人臉模型載入失敗。請檢查網路後重試。" + ((e as Error).message ?? ""));
        setPhase("error");
      }
    })();
    return () => { cancelled = true; };
  }, [phase]);

  // ── Step 3: acquire camera stream + bind to <video> ───────────────────
  // The <video> element is now ALWAYS mounted (the unified
  // FaceFrame renders it in every non-terminal phase), so we can bind
  // the stream synchronously here instead of in a follow-up effect
  // gated on "ready". One effect, no intermediate phase between
  // "stream acquired" and "stream attached" — eliminates the flash
  // where the video element existed but had no srcObject.
  useEffect(() => {
    if (phase !== "camera_starting") return;
    let cancelled = false;
    void (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          if (video.srcObject !== stream) video.srcObject = stream;
          // iOS may block autoplay; the capture rAF loop polls
          // readyState so a quietly-rejected play() is fine.
          void video.play().catch(() => { /* */ });
        }
        setPhase("ready");
      } catch (e) {
        if (cancelled) return;
        const m = (e as Error).name === "NotAllowedError"
          ? "需要相機存取權限才能完成註冊。請在瀏覽器設定允許後重試。"
          : "無法開啟相機：" + ((e as Error).message ?? "");
        setErrMsg(m);
        setPhase("error");
      }
    })();
    return () => { cancelled = true; };
  }, [phase]);

  // ── Step 4: continuous capture loop ──────────────────────────────────
  // Order-independent. On each frame we compute the head's yaw/pitch
  // direction angle, find the *uncaptured* POSE whose check function
  // matches AND is closest to that angle, and capture it. User can
  // rotate clockwise / counter-clockwise / skip around — segments fill
  // as their direction is hit. Once all 8 are captured we flip to
  // submitting.
  useEffect(() => {
    if (phase !== "ready" && phase !== "capturing") return;
    let stopped = false;
    let raf = 0;

    const tick = async () => {
      if (stopped || !videoRef.current || !window.faceapi) return;
      if (videoRef.current.readyState < 2) {
        raf = requestAnimationFrame(tick);
        return;
      }
      try {
        const det = await window.faceapi
          .detectSingleFace(videoRef.current, new window.faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 }))
          .withFaceLandmarks()
          .withFaceDescriptor();
        if (!stopped && det && phase === "ready") {
          const { yaw, pitch } = estimatePose(det.landmarks.positions);
          // Pointer = where the head is aiming, expressed as an angle on
          // the SVG ring. Magnitude must exceed POINTER_MIN_MAG so a
          // straight-ahead head doesn't drift the pointer around with
          // noise. Below that threshold pointer is null (hidden).
          const magnitude = Math.hypot(yaw, pitch);
          let headAngleDeg: number | null = null;
          if (magnitude >= POINTER_MIN_MAG) {
            headAngleDeg = (Math.atan2(pitch, yaw) * 180 / Math.PI + 360) % 360;
          }
          setPointerAngle(headAngleDeg);

          // Suggest the closest uncaptured pose to the pointer (if any).
          // Falls back to "first uncaptured" when pointer is null.
          const uncapturedIdxs = POSES
            .map((_, i) => i)
            .filter((i) => !descriptorsRef.current[i]);
          if (uncapturedIdxs.length === 0) {
            setNextIdx(-1);
          } else if (headAngleDeg == null) {
            setNextIdx(uncapturedIdxs[0]);
          } else {
            const sorted = uncapturedIdxs
              .map((i) => ({ i, d: angularDist(POSES[i].angleDeg, headAngleDeg!) }))
              .sort((a, b) => a.d - b.d);
            setNextIdx(sorted[0].i);
          }

          // Capture: pointer overlapping an uncaptured segment triggers
          // capture. CAPTURE_HALF matches the visible arc half-width so
          // it fires the moment the pointer enters the segment visually.
          if (headAngleDeg != null) {
            for (const i of uncapturedIdxs) {
              if (angularDist(POSES[i].angleDeg, headAngleDeg) > CAPTURE_HALF) continue;
              descriptorsRef.current[i] = det.descriptor;
              thumbnailsRef.current[i] = grabThumbnail(videoRef.current, det.detection.box);
              const nextCaptured = [...captured, i];
              setCaptured(nextCaptured);
              setFlashIdx(i);
              setTimeout(() => setFlashIdx((cur) => (cur === i ? null : cur)), 700);
              if (nextCaptured.length >= POSES.length) {
                setPhase("submitting");
                return;
              }
              // brief pause to avoid double-capture on the same frame
              await new Promise((r) => setTimeout(r, 700));
              break;
            }
          }
        }
      } catch { /* ignore transient detect errors */ }
      if (!stopped) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => { stopped = true; cancelAnimationFrame(raf); };
  }, [phase, captured]);

  // ── Step 5: submit ───────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== "submitting") return;
    void (async () => {
      try {
        // The backend expects exactly POSES.length descriptors at the
        // expected index positions. By the time we reach submitting,
        // captured.length === POSES.length so each ref slot is filled,
        // but cast / assert anyway for safety.
        const descriptors = descriptorsRef.current.map((d) => d ? Array.from(d) : []);
        const thumbnails = thumbnailsRef.current.map((t) => t ?? "");
        const r = await fetch(`${API_BASE}/v1/face/enroll/finish`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ token, descriptors, thumbnails }),
        });
        const j = await r.json().catch(() => ({})) as { ok?: true; error?: string };
        if (!r.ok) {
          setErrMsg(`提交失敗（${j.error ?? r.status}）`);
          setPhase("error");
          return;
        }
        setPhase("done");
        // stop camera
        streamRef.current?.getTracks().forEach((t) => t.stop());
      } catch (e) {
        setErrMsg("提交時連線中斷，請重試。" + ((e as Error).message ?? ""));
        setPhase("error");
      }
    })();
  }, [phase, token]);

  // ── unmount cleanup ──────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  return (
    <div className="min-h-[100dvh] bg-bg-base text-text-primary flex flex-col items-center px-4 py-8">
      <header className="flex items-center gap-2 mb-5">
        <img
          src="/sentry-icon.svg"
          alt="SENTRY"
          className="h-8 w-auto"
          style={{ filter: "brightness(0) invert(1)" }}
        />
        <span className="text-sm font-semibold tracking-wide">NEXUS</span>
      </header>

      <main className="w-full max-w-md card bg-bg-surface border-border p-6 space-y-5 animate-fade-in">
        <div>
          <h1 className="text-xl font-semibold tracking-tight inline-flex items-center gap-2">
            <Smile className="h-5 w-5 text-accent" />
            人臉註冊
          </h1>
          {officerName && (
            <p className="mt-1 text-xs text-text-muted">建立人員：<span className="text-text-primary">{officerName}</span></p>
          )}
          {alreadyEnrolled && phase !== "done" && (
            <p className="mt-2 text-xs text-warning bg-warning/10 rounded p-2">
              此人員已完成過註冊，再次提交將覆蓋既有人臉資料。
            </p>
          )}
        </div>

        {phase === "done" ? (
          <DonePane name={officerName} />
        ) : phase === "error" ? (
          <ErrorPane message={errMsg ?? "發生未知錯誤"} />
        ) : (
          <FaceFrame
            phase={phase}
            videoRef={videoRef}
            canvasRef={canvasRef}
            videoReady={videoReady}
            onVideoReady={() => setVideoReady(true)}
            captured={captured}
            nextIdx={nextIdx}
            flashIdx={flashIdx}
            pointerAngle={pointerAngle}
            mlStages={mlStages}
          />
        )}

        <p className="text-2xs text-text-muted text-center">
          您的人臉影像不會離開本裝置；伺服器只儲存 128 個浮點數的特徵向量。
        </p>
      </main>
    </div>
  );
}

// ── sub-components ───────────────────────────────────────────────────────

const ML_STAGE_LABEL: Record<MlStage, string> = {
  lib: "人臉辨識套件",
  detector: "臉部偵測模型",
  landmark: "特徵點模型（68）",
  recognition: "特徵向量模型",
};
const ML_STAGE_ORDER: MlStage[] = ["lib", "detector", "landmark", "recognition"];

/**
 * Single component that occupies the camera area across EVERY non-
 * terminal phase. Behaviour by phase:
 *
 *   - init / validating          — circle overlay: spinner + 「驗證連結中…」
 *   - loading_ml                 — circle overlay: per-stage progress
 *                                   (X/4 + bar + current stage name)
 *   - camera_starting            — circle overlay: spinner + 「啟動相機中…」
 *   - ready / capturing          — video visible + FaceID tick ring +
 *                                   pointer + cross-hair guide
 *   - submitting                 — video stays visible, dim overlay +
 *                                   「上傳人臉資料中…」
 *
 * The <video>, <canvas>, and aspect-square layout are always mounted —
 * no remounts between phases. That's both a UX win (no layout reflow
 * when the operator's eye is locked on the centre of the screen) and
 * a stability win (the camera stream binds once, plays once, and isn't
 * fighting React remount cycles during getUserMedia's initial
 * resolution negotiation).
 */
function FaceFrame(props: {
  phase: Phase;
  videoRef: React.RefObject<HTMLVideoElement>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  videoReady: boolean;
  onVideoReady: () => void;
  captured: number[];
  nextIdx: number;
  flashIdx: number | null;
  pointerAngle: number | null;
  mlStages: Record<MlStage, StageState>;
}): React.ReactElement {
  const {
    phase, videoRef, canvasRef, videoReady, onVideoReady,
    captured, nextIdx, flashIdx, pointerAngle, mlStages,
  } = props;

  const isCapturePhase = phase === "ready" || phase === "capturing";
  // Video is opacity-faded in only when both the stream metadata has
  // loaded (first stable frame) AND we're in a capture-eligible phase.
  // Pre-`onloadedmetadata` browsers occasionally show a momentary
  // low-res zoom-in while resolution negotiates; the fade hides that.
  const showVideo = isCapturePhase && videoReady;

  const capturedSet = new Set(captured);
  const total = POSES.length;
  const prompt = nextIdx < 0
    ? "已完成所有角度，正在上傳…"
    : POSES[nextIdx]?.hint ?? "請慢慢轉動頭部";

  // FaceID-style geometry — see TICK_COUNT / POSE_COVERAGE_DEG comments
  // for capture/visualisation math. 8 capture POSES at 45° each; 120
  // ticks at 3° each fill the ring as the operator covers them.
  const CAMERA_R = 38;
  const TICK_COUNT = 120;
  const TICK_INNER_R = 42;
  const TICK_OUTER_R = 49;
  const TICK_STROKE_W = 1.4;
  const POSE_COVERAGE_DEG = 22.5;

  // Map captured pose set → which ticks are filled. A tick at angle θ
  // is "covered" if any captured pose is within POSE_COVERAGE_DEG.
  const captureAngles = useMemo(
    () => captured.map((idx) => POSES[idx].angleDeg),
    [captured],
  );
  const flashAngle = flashIdx != null ? POSES[flashIdx].angleDeg : null;

  return (
    <div className="space-y-3">
      {/* One-shot keyframes for the per-tick flash on capture. */}
      <style>{`
        @keyframes face-enroll-tick-flash {
          0%   { stroke: #22c55e; filter: drop-shadow(0 0 1px #22c55e); }
          40%  { stroke: #86efac; filter: drop-shadow(0 0 3px #22c55e); }
          100% { stroke: #22c55e; filter: drop-shadow(0 0 1px #22c55e); }
        }
      `}</style>

      <div className="aspect-square w-full relative">
        {/* Camera circle — always rendered so phase swaps don't reflow
            the panel layout. Video opacity is gated on (a) being in a
            capture phase and (b) the stream's first metadata event so
            the initial low-res zoom-in is hidden behind the init
            overlay. */}
        <div className="absolute rounded-full overflow-hidden bg-black"
             style={{ inset: "12%" }}>
          <video
            ref={videoRef}
            onLoadedMetadata={onVideoReady}
            className={`w-full h-full object-cover scale-x-[-1] transition-opacity duration-300 ${showVideo ? "opacity-100" : "opacity-0"}`}
            playsInline
            muted
          />
          {/* Init-phase overlay sits over the camera circle while the
              video is invisible. Spinner / progress for each phase; the
              whole stack is inside the rounded clipper so it visually
              "is" the camera area. */}
          {!showVideo && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center bg-bg-base/95">
              <InitOverlay phase={phase} mlStages={mlStages} />
            </div>
          )}
          {/* Submitting overlay — video stays underneath for visual
              continuity (operator just finished the rotation; sudden
              swap to a blank panel feels jarring). Dim + spinner +
              text. */}
          {phase === "submitting" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center bg-bg-base/70 backdrop-blur-sm">
              <Loader2 className="h-7 w-7 animate-spin text-accent" />
              <p className="text-xs text-text-secondary">上傳人臉資料中…</p>
            </div>
          )}
        </div>
        <canvas ref={canvasRef} className="hidden" />

        {/* FaceID-style ring — only shown during capture phases. Each
            tick is filled green if any captured pose is within
            POSE_COVERAGE_DEG of its angle, grey otherwise. The grey gap
            that remains as the user rotates IS the implicit "rotate
            here next" cue (no separate pulsing pose marker). */}
        {isCapturePhase && (
          <svg
            viewBox="0 0 100 100"
            className="absolute inset-0 w-full h-full pointer-events-none"
            preserveAspectRatio="xMidYMid meet"
          >
            {Array.from({ length: TICK_COUNT }, (_, i) => {
              const angleDeg = (i / TICK_COUNT) * 360;
              const rad = angleDeg * Math.PI / 180;
              const x1 = 50 + TICK_INNER_R * Math.cos(rad);
              const y1 = 50 + TICK_INNER_R * Math.sin(rad);
              const x2 = 50 + TICK_OUTER_R * Math.cos(rad);
              const y2 = 50 + TICK_OUTER_R * Math.sin(rad);
              const minDist = captureAngles.length === 0
                ? Infinity
                : Math.min(...captureAngles.map((a) => angularDist(a, angleDeg)));
              const covered = minDist <= POSE_COVERAGE_DEG;
              const inFlash = flashAngle != null && angularDist(flashAngle, angleDeg) <= POSE_COVERAGE_DEG;
              const stroke = covered ? "#22c55e" : "rgba(255,255,255,0.22)";
              return (
                <line
                  key={i}
                  x1={x1} y1={y1} x2={x2} y2={y2}
                  stroke={stroke}
                  strokeWidth={TICK_STROKE_W}
                  strokeLinecap="round"
                  style={{
                    transition: "stroke 250ms ease",
                    filter: covered ? "drop-shadow(0 0 1px #22c55e)" : undefined,
                    animation: inFlash ? "face-enroll-tick-flash 700ms ease-out 1" : undefined,
                  }}
                />
              );
            })}

            {/* Subtle vertical cross-hair guide over the camera — Apple
                FaceID-style. Helps the user line their nose up before
                rotating. Drawn faint so it doesn't fight with the face
                feed underneath. */}
            <line
              x1={50} y1={50 - CAMERA_R} x2={50} y2={50 + CAMERA_R}
              stroke="rgba(255,255,255,0.18)"
              strokeWidth={0.6}
              strokeLinecap="round"
            />

            {/* Head-direction pointer — small marker on the tick ring at
                the user's current head angle. Apple hides this (the
                uncovered grey gap is the implicit pointer), but our
                face-api pose estimator is jittery enough that an
                explicit "where you are" indicator pays for itself. */}
            {pointerAngle != null && (() => {
              const rad = pointerAngle * Math.PI / 180;
              const r = (TICK_INNER_R + TICK_OUTER_R) / 2;
              const px = 50 + r * Math.cos(rad);
              const py = 50 + r * Math.sin(rad);
              return (
                <g
                  style={{
                    transform: `translate(${px - 50}px, ${py - 50}px)`,
                    transition: "transform 120ms ease-out",
                  }}
                >
                  <circle cx={50} cy={50} r={1.8}
                    fill="#ffffff"
                    stroke="#00d4ff"
                    strokeWidth={0.8}
                    style={{ filter: "drop-shadow(0 0 2px #00d4ff)" }}
                  />
                </g>
              );
            })()}
          </svg>
        )}

        {/* Centered counter chip overlaying the camera — only during
            capture. */}
        {isCapturePhase && (
          <div className="absolute inset-x-0 bottom-[14%] flex items-center justify-center pointer-events-none">
            <span className="rounded-full bg-bg-base/80 backdrop-blur-sm px-2.5 py-0.5 text-2xs font-mono tabular-nums text-text-primary border border-border-subtle">
              {captured.length} / {total}
            </span>
          </div>
        )}
      </div>

      {/* Prompt below the circle — only meaningful once the operator is
          actively rotating. Hidden during init phases so the camera
          area is the sole focal point. */}
      {isCapturePhase && (
        <>
          <p className="text-center text-base font-semibold text-text-primary">
            {prompt}
          </p>
          <p className="text-center text-2xs text-text-muted inline-flex items-center justify-center gap-1 w-full">
            <Camera className="h-3 w-3" /> 偵測到正確角度時自動拍攝（{captured.length} / {total} 角度）
          </p>
        </>
      )}
    </div>
  );
}

/**
 * Status content shown inside the camera circle while we're not yet
 * in a capture-eligible phase. Lives separately because each branch
 * has a slightly different layout (spinner vs progress bar + stage
 * list) and the inline ternary was getting unwieldy.
 */
function InitOverlay({
  phase,
  mlStages,
}: {
  phase: Phase;
  mlStages: Record<MlStage, StageState>;
}): React.ReactElement {
  if (phase === "loading_ml") {
    const done = ML_STAGE_ORDER.filter((k) => mlStages[k] === "done").length;
    const total = ML_STAGE_ORDER.length;
    const currentLoading = ML_STAGE_ORDER.find((k) => mlStages[k] === "loading");
    const failed = ML_STAGE_ORDER.find((k) => mlStages[k] === "failed");
    const pct = Math.round((done / total) * 100);
    return (
      <div className="flex flex-col items-center gap-2 w-full">
        {failed
          ? <AlertTriangle className="h-7 w-7 text-danger" />
          : <Loader2 className="h-7 w-7 animate-spin text-accent" />}
        <p className="text-xs font-medium text-text-primary">
          載入人臉模型 <span className="font-mono tabular-nums">{done}/{total}</span>
        </p>
        <p className="text-2xs text-text-muted">
          {failed
            ? `${ML_STAGE_LABEL[failed]} 失敗`
            : currentLoading
              ? ML_STAGE_LABEL[currentLoading]
              : "準備中…"}
        </p>
        <div className="h-1 w-full max-w-[70%] rounded-full bg-bg-raised overflow-hidden mt-1">
          <div
            className="h-full bg-accent transition-[width] duration-300 ease-out"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    );
  }
  const label =
    phase === "camera_starting" ? "啟動相機中…"
      : "驗證連結中…";   // init / validating
  return (
    <div className="flex flex-col items-center gap-3">
      <Loader2 className="h-7 w-7 animate-spin text-accent" />
      <p className="text-xs text-text-secondary">{label}</p>
    </div>
  );
}

function DonePane({ name }: { name: string | null }): React.ReactElement {
  return (
    <div className="space-y-3">
      <div className="rounded-md border border-success/40 bg-success/10 p-4 text-sm text-success flex items-start gap-2">
        <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0" />
        <div>
          <p className="font-medium">人臉註冊完成</p>
          <p className="text-xs mt-1 opacity-90">
            {name ? `${name} 的` : "您的"}人臉特徵向量已儲存。
            未來巡邏需要驗證身分時會請您再次入鏡。
          </p>
        </div>
      </div>
      <p className="text-2xs text-text-muted text-center">您現在可以關閉此頁面。</p>
    </div>
  );
}

function ErrorPane({ message }: { message: string }): React.ReactElement {
  return (
    <div className="rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger flex items-start gap-2">
      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────────────────

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
    if (existing) {
      if (window.faceapi) return resolve();
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("script load failed")), { once: true });
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("script load failed"));
    document.head.appendChild(s);
  });
}

/** Estimate yaw + pitch (degrees) from face-api.js 68-point landmarks.
 *  Heuristic; good enough for FaceID-style pose gating. */
function estimatePose(landmarks: { x: number; y: number }[]): { yaw: number; pitch: number } {
  if (landmarks.length < 68) return { yaw: 0, pitch: 0 };
  // 68-pt model layout:
  //   0..16 jaw / 17..21 right brow / 22..26 left brow / 27..30 nose bridge
  //   31..35 nose bottom / 36..41 right eye / 42..47 left eye
  //   48..67 mouth
  const noseTip = landmarks[30];
  const leftEye = avg(landmarks.slice(36, 42));    // subject's right (mirrored)
  const rightEye = avg(landmarks.slice(42, 48));   // subject's left
  const chin = landmarks[8];
  const foreheadProxy = avg(landmarks.slice(17, 27)); // brows = forehead proxy

  // yaw: horizontal asymmetry of eyes around nose
  const eyeMidX = (leftEye.x + rightEye.x) / 2;
  const yawNorm = (noseTip.x - eyeMidX) / Math.max(1, Math.abs(rightEye.x - leftEye.x));
  // face-api.js detects landmarks in the un-mirrored image. When the
  // subject turns their head to their RIGHT, the nose tip moves to
  // the LEFT of the image (negative noseTip.x - eyeMidX). The video
  // shown to the user is mirrored (scale-x: -1), so the same right
  // turn looks like the face moves to the right of the screen, which
  // is where the "右" segment is. We negate yawNorm here so the
  // resulting yaw sign matches the SCREEN direction, not the raw
  // image direction — i.e. positive yaw = subject's right = screen
  // right = "右" segment.
  const yaw = -yawNorm * 90;

  // pitch: vertical position of nose between forehead and chin
  const total = chin.y - foreheadProxy.y;
  const noseProgress = total > 0 ? (noseTip.y - foreheadProxy.y) / total : 0.5;
  // Empirically noseProgress sits at ≈ 0.55 when looking straight ahead;
  // looking up pulls it < 0.45, looking down pushes it > 0.65 (roughly
  // ±0.10 of usable swing). The multiplier converts that to a degree-
  // equivalent value comparable to `yaw`. Originally 200 → pitch range
  // ±20°, but `yaw` ranges ±45° naturally so atan2(pitch, yaw) for a
  // "look down" gesture (90°) was getting yanked far off-target by tiny
  // yaw twitches (~5–10°). Operators reported 左下 / 下 / 右下 as
  // particularly hard to capture. Bumping the multiplier to 320 puts
  // pitch's range roughly on par with yaw's so atan2 lands close to the
  // intended cardinal/diagonal even with normal head jitter.
  const pitch = (noseProgress - 0.55) * 320;

  return { yaw, pitch };
}

function avg(pts: { x: number; y: number }[]): { x: number; y: number } {
  let sx = 0, sy = 0;
  for (const p of pts) { sx += p.x; sy += p.y; }
  return { x: sx / pts.length, y: sy / pts.length };
}

/** Crop the face box out of the video element, encode as JPEG data URL. */
function grabThumbnail(video: HTMLVideoElement, box: { x: number; y: number; width: number; height: number }): string {
  const size = 160;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";
  // Mirror the source X to match the video's CSS scale-x(-1).
  ctx.translate(size, 0);
  ctx.scale(-1, 1);
  ctx.drawImage(
    video,
    box.x, box.y, box.width, box.height,
    0, 0, size, size,
  );
  return canvas.toDataURL("image/jpeg", 0.8);
}
